import React from "react";
import { isStandaloneTabletLayout } from "../../../utils/device";
import { getStoredSidebarCollapsed } from "../../../utils/navigation/sidebarCollapsed";
import { Skeleton } from "../../ui/skeleton";

const NAV_ITEMS = ["a", "b", "c", "d", "e", "f", "g"] as const;
const NAV_FOOTER = ["a", "b", "c", "d"] as const;
const LIST_ROWS = ["a", "b", "c", "d", "e", "f", "g"] as const;
const BLOCKS = ["a", "b", "c"] as const;

// Routes whose real page is a bookings list beside a main panel.
const isListPage = (pathname: string) =>
  /^\/(diary|bookings|sites)(\/|$)/.test(pathname);

const HeaderSkeleton = () => (
  <div className="flex h-[60px] shrink-0 items-center gap-4 px-4">
    <Skeleton className="h-9 w-[120px] rounded-full" />
    <Skeleton className="h-9 w-[300px] rounded-md" />
    <div className="flex-1" />
    <Skeleton className="h-9 w-[200px] rounded-md" />
    <Skeleton className="h-9 w-[200px] rounded-md" />
  </div>
);

const NavSkeleton = ({ collapsed }: { collapsed: boolean }) =>
  collapsed ? (
    <div className="ml-3 mt-3 flex w-[72px] shrink-0 flex-col items-center gap-5 rounded-lg border border-border bg-card py-5">
      {NAV_ITEMS.map((id) => (
        <Skeleton key={`nav-${id}`} className="size-6 rounded" />
      ))}
    </div>
  ) : (
    <div className="ml-3 mt-3 flex w-[256px] shrink-0 flex-col gap-1 rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-3 border-b border-border pb-4">
        <Skeleton className="size-9 rounded-md" />
        <Skeleton className="h-5 w-24" />
      </div>
      {NAV_ITEMS.map((id) => (
        <div key={`nav-${id}`} className="flex items-center gap-3 py-3">
          <Skeleton className="size-5 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
      ))}
      <div className="flex-1" />
      {NAV_FOOTER.map((id) => (
        <div key={`foot-${id}`} className="flex items-center gap-3 py-3">
          <Skeleton className="size-5 rounded" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );

const ListSkeleton = () => (
  <div className="flex w-[333px] shrink-0 flex-col gap-3 border-r border-border p-4">
    <Skeleton className="h-9 w-full rounded-md" />
    <Skeleton className="h-9 w-3/4 rounded-md" />
    {LIST_ROWS.map((id) => (
      <div key={`row-${id}`} className="flex items-start gap-3">
        <Skeleton className="h-14 w-3 rounded" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
        </div>
        <Skeleton className="h-4 w-12" />
      </div>
    ))}
  </div>
);

const MainSkeleton = () => (
  <div className="flex min-w-0 flex-1 flex-col gap-4 p-6">
    <Skeleton className="h-10 w-64" />
    {BLOCKS.map((id) => (
      <Skeleton key={`block-${id}`} className="h-32 w-full rounded-lg" />
    ))}
  </div>
);

/**
 * Cold-load placeholder for a route. It has the shape of the app shell (header,
 * navigation rail, bordered panel and, for the diary and booking routes, the
 * bookings list) so the page does not jump when the real shell replaces it.
 * The tablet shell has no navigation rail (PageContainer only renders it for
 * `!isTablet`), so the skeleton drops it there too.
 */
export function PageLoader() {
  const withList = isListPage(window.location.pathname);
  const isTablet = isStandaloneTabletLayout();

  return (
    <div
      aria-busy="true"
      aria-label="Loading"
      className="flex h-screen w-full flex-col bg-page-background"
    >
      <HeaderSkeleton />
      <div className="flex min-h-0 flex-1">
        {!isTablet && <NavSkeleton collapsed={getStoredSidebarCollapsed()} />}
        <div
          className={`flex min-w-0 flex-1 overflow-hidden rounded-tl-lg border border-border bg-card ${isTablet ? "" : "ml-3 mt-3"}`}
        >
          {withList && <ListSkeleton />}
          <MainSkeleton />
        </div>
      </div>
    </div>
  );
}
